import { useParams } from 'react-router-dom';
import {
  FiCalendar, FiClock, FiHome, FiAlertCircle, FiPaperclip, FiCheck,
  FiSearch, FiActivity, FiFileText,
} from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { acompanhamentoPublicoService, type AcompanhamentoPublico } from '../../services/solicitacoes.service';
import { useDadosVivos } from '../../hooks/useDadosVivos';
import { formatarDataBR, formatarDataHoraBR } from '../../lib/data';

const STATUS_LABEL: Record<string, string> = {
  rascunho: 'Rascunho', aberto: 'Aberto', em_tramitacao: 'Em tramitação',
  aguardando_solicitante: 'Aguardando solicitante', aguardando_aceite: 'Aguardando aceite',
  em_analise: 'Em análise', em_diligencia: 'Em diligência',
  deferido: 'Deferido', indeferido: 'Indeferido',
  arquivado: 'Arquivado', cancelado: 'Cancelado', concluido: 'Concluído',
};

export function AcompanharProtocoloPublicoPage() {
  // Rota splat ("/protocolo/*"): o número contém barra (ex.: ZEL-0002/2026), então o
  // hash é o ÚLTIMO segmento da URL e o número é todo o resto.
  const tail = useParams()['*'] ?? '';
  const corte = tail.lastIndexOf('/');
  const numero = corte >= 0 ? tail.slice(0, corte) : undefined;
  const hash = corte >= 0 ? tail.slice(corte + 1) : undefined;

  const { dados, carregando, erro } = useDadosVivos<AcompanhamentoPublico>(
    async () => {
      const r = await acompanhamentoPublicoService.buscar(numero!, hash!);
      if ('erro' in r) throw new Error(r.erro);
      return r;
    },
    [numero, hash],
    { ativo: !!numero && !!hash },
  );

  if (carregando) {
    return (
      <PortalPage
        titulo="Acompanhar protocolo"
        subtitulo="Consulte a situação e o histórico do seu protocolo."
        icone={<FiSearch size={26} />}
      >
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (erro || !dados) {
    return <PainelErro mensagem={erro ?? 'Protocolo não encontrado.'} />;
  }

  const finalizado = ['concluido', 'arquivado', 'cancelado', 'deferido', 'indeferido'].includes(dados.documento.status);

  return (
    <PortalPage
      titulo={`Protocolo ${dados.documento.numero}`}
      subtitulo={dados.estabelecimento.nome}
      icone={<FiSearch size={26} />}
    >
      <div className="space-y-5">
        {/* Situação */}
        <SectionCard
          titulo="Situação do protocolo"
          icone={<FiFileText size={18} />}
        >
          <div className="space-y-5">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Status</div>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${finalizado ? 'bg-emerald-500' : 'bg-amber-400'}`}
                />
                <span className="text-base font-semibold text-slate-900">
                  {STATUS_LABEL[dados.documento.status] ?? dados.documento.status}
                </span>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Assunto</div>
              <h2 className="text-lg font-semibold text-slate-900 mt-0.5">{dados.documento.titulo}</h2>
              {dados.assunto.nome && (
                <p className="text-sm text-slate-600 mt-1">{dados.assunto.nome}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <FiCalendar size={13} /> Aberto em {formatarDataBR(dados.documento.data_abertura)}
              </span>
              {dados.documento.data_limite && (
                <span className="flex items-center gap-1">
                  <FiClock size={13} /> Prazo {formatarDataBR(dados.documento.data_limite)}
                </span>
              )}
              {dados.documento.data_conclusao && (
                <span className="flex items-center gap-1 text-emerald-700">
                  <FiCheck size={13} /> Concluído em {formatarDataBR(dados.documento.data_conclusao)}
                </span>
              )}
            </div>

            {dados.documento.descricao && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Descrição</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{dados.documento.descricao}</p>
              </div>
            )}
          </div>
        </SectionCard>

        {/* Linha do tempo */}
        <SectionCard
          titulo={`Linha do tempo (${dados.historico.length})`}
          icone={<FiActivity size={18} />}
        >
          {dados.historico.length === 0 ? (
            <p className="text-sm text-slate-500">Sem movimentações públicas registradas.</p>
          ) : (
            <ul className="space-y-4">
              {dados.historico.map((h, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-institutional mt-1.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="text-sm font-medium text-slate-900">{h.titulo ?? formatarTipo(h.tipo)}</div>
                      <div className="text-xs text-slate-500">{formatarDataHoraBR(h.criado_em)}</div>
                    </div>
                    <div className="text-xs text-slate-500">{h.ator_nome}</div>
                    {h.mensagem && (
                      <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{h.mensagem}</div>
                    )}
                    {(h.setor_origem_nome || h.setor_destino_nome) && (
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <FiHome size={11} /> {h.setor_origem_nome} → {h.setor_destino_nome}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Anexos */}
        {dados.anexos.length > 0 && (
          <SectionCard
            titulo={`Anexos públicos (${dados.anexos.length})`}
            icone={<FiPaperclip size={18} />}
          >
            <ul className="border border-slate-200 rounded divide-y divide-slate-100">
              {dados.anexos.map((a, idx) => (
                <li key={idx} className="flex items-center gap-3 p-3">
                  <FiPaperclip className="text-slate-400 flex-shrink-0" size={14} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{a.nome_original}</div>
                    <div className="text-xs text-slate-500">{(a.tamanho_bytes / 1024).toFixed(1)} KB</div>
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
          Esta é a página pública de acompanhamento do protocolo. Para enviar respostas, anexos
          ou ver dados privados, é necessário fazer login no portal.
        </div>
      </div>
    </PortalPage>
  );
}

function formatarTipo(tipo: string): string {
  const map: Record<string, string> = {
    criacao: 'Solicitação aberta',
    encaminhamento: 'Encaminhamento',
    despacho: 'Despacho',
    resposta: 'Resposta do solicitante',
    anexo_adicionado: 'Anexo adicionado',
    mudanca_status: 'Mudança de status',
    conclusao: 'Concluído',
    arquivamento: 'Arquivado',
    observacao: 'Observação',
  };
  return map[tipo] ?? tipo;
}

function PainelErro({ mensagem }: { mensagem: string }) {
  const map: Record<string, string> = {
    protocolo_nao_encontrado: 'Protocolo não encontrado. Verifique o número e o link de acompanhamento.',
    modulo_nao_publico: 'Este protocolo não permite acompanhamento público.',
  };
  const texto = map[mensagem] ?? mensagem;

  return (
    <PainelResultado
      titulo="Não foi possível abrir o protocolo"
      descricao={texto}
      icone={<FiAlertCircle size={32} />}
      tom="aviso"
    />
  );
}
